import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Tv2, LineChart, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/board", label: "Pipeline", icon: KanbanSquare },
  { to: "/channels", label: "Channels", icon: Tv2 },
  { to: "/performance", label: "Performance", icon: LineChart },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-display font-extrabold">C</div>
          <div>
            <p className="font-extrabold text-lg tracking-tight leading-none no-underline" style={{ fontFamily: "'Manrope', sans-serif" }}>Channel<span className="text-primary">HQ</span></p>
            <p className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] uppercase mt-1">Command Center</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`
            }
          >
            <n.icon size={18} strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-medium truncate">{user?.name || "Creator"}</p>
          <p className="font-mono text-[11px] text-muted-foreground truncate">{user?.email}</p>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative" style={{ background: "radial-gradient(1200px 600px at 50% -5%, #16130B 0%, #0C0B0C 42%, #0A0A0B 100%)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#121214] border-r border-border z-20">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#121214] border-b border-border">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-display font-extrabold text-sm">C</div>
          <span className="font-extrabold tracking-tight no-underline" style={{ fontFamily: "'Manrope', sans-serif" }}>Channel<span className="text-primary">HQ</span></span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 text-muted-foreground">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#121214] border-r border-border" onClick={(e) => e.stopPropagation()}>
            <SidebarInner />
          </aside>
        </div>
      )}

      <main className="lg:pl-64 relative z-10">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-6 sm:py-8">{children}</div>
      </main>
    </div>
  );
}
